// hooks/useAgGridReduxState.ts
import type { ColumnState, GridApi, GridReadyEvent, ColumnResizedEvent } from "ag-grid-enterprise";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { rSetAgGridState, selectAgGridStateByKey } from "../agGridStateSlice";

/** Bump this if you change the semantics of the stored grid state */
const GRID_STATE_VERSION = 1;

function useDebounced(fn: () => void, delay = 250) {
  const t = useRef<number | null>(null);
  return useCallback(() => {
    if (t.current != null) window.clearTimeout(t.current);
    t.current = window.setTimeout(() => fn(), delay);
  }, [fn, delay]);
}

export interface UseAgGridReduxStateOptions {
  /** All currently available column IDs (must match colId in ColDefs) */
  availableColIds: string[];
  /** Signature that changes when your column *set* changes (e.g. hash of colIds) */
  columnsSignature: string;
  /** Whether to track/apply pivot mode (default: false) */
  managePivotMode?: boolean;
}

/**
 * Hook that keeps AG Grid state in Redux (and optionally mirrored to LS via middleware).
 * - Applies saved state on grid ready (only if version & signature match).
 * - Reconciles saved columnState with currently available columns.
 * - Persists on all relevant grid events (debounced).
 */
export function useAgGridReduxState(key: string, opts: UseAgGridReduxStateOptions) {
  const { availableColIds, columnsSignature, managePivotMode = false } = opts;

  const dispatch = useAppDispatch();
  const saved = useAppSelector(selectAgGridStateByKey(key));
  const apiRef = useRef<GridApi | null>(null);

  /** Build a fast lookup set for current columns */
  const availableSet = useMemo(() => new Set(availableColIds), [availableColIds]);

  /** Reconcile saved ColumnState with the currently available colIds. */
  const reconcileColumnState = useCallback(
    (state: ColumnState[] | null | undefined): ColumnState[] => {
      const out: ColumnState[] = [];
      const seen = new Set<string>();

      // 1) Keep only existing columns, preserve their order/state.
      if (Array.isArray(state)) {
        for (const s of state) {
          if (s.colId && availableSet.has(s.colId) && !seen.has(s.colId)) {
            out.push({ ...s });
            seen.add(s.colId);
          }
        }
      }

      // 2) Append any new columns with default state at the end.
      for (const id of availableSet) {
        if (!seen.has(id)) {
          out.push({ colId: id }); // default visibility/order/width
        }
      }

      return out;
    },
    [availableSet]
  );

  /** Persist the current grid state into Redux */
  const persist = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;

    const next = {
      version: GRID_STATE_VERSION,
      columnsSignature,
      quickFilter: (api.getGridOption("quickFilterText") as string) ?? "",
      columnState: api.getColumnState(),
      filterModel: api.getFilterModel(),
      pivotMode: undefined,
    };

    dispatch(rSetAgGridState({ key, data: next }));
  }, [dispatch, key, columnsSignature, managePivotMode]);

  const onStateUpdated = useDebounced(persist, 250);

  /** Apply saved state (if compatible) on grid ready */
  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      apiRef.current = params.api;

      const api = params.api;

      const versionOk = saved?.version === GRID_STATE_VERSION;
      const signatureOk = saved?.columnsSignature && saved.columnsSignature === columnsSignature;

      // Only apply column state if both version and signature match
      const shouldApplyColumns = !!(versionOk && signatureOk && saved?.columnState);

      if (shouldApplyColumns) {
        const reconciled = reconcileColumnState(saved!.columnState!);
        api.applyColumnState({
          state: reconciled,
          applyOrder: true,
        });
      }

      if (saved?.filterModel != null) {
        api.setFilterModel(saved.filterModel);
      }

      if (saved?.quickFilter != null) {
        api.setGridOption("quickFilterText", saved.quickFilter);
      }

      //   if (managePivotMode && typeof saved?.pivotMode === "boolean") {
      //     api.setPivotMode(saved.pivotMode);
      //   }
    },
    [
      saved?.version,
      saved?.columnsSignature,
      saved?.columnState,
      saved?.filterModel,
      saved?.quickFilter,
      saved?.pivotMode,
      columnsSignature,
      managePivotMode,
      reconcileColumnState,
    ]
  );

  /** Reset filters when Redux flag says so (your slice can toggle this) */
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !saved?.resetFilters) return;

    api.setFilterModel(null);
    api.setGridOption("quickFilterText", "");

    // Clear the flag; keep other saved state intact.
    dispatch(
      rSetAgGridState({
        key,
        data: { resetFilters: false, filterModel: null, quickFilter: "" },
      })
    );
  }, [saved?.resetFilters, dispatch, key]);

  /** Convenience setter for quick filter */
  const setQuickFilterText = useCallback(
    (text: string) => {
      const api = apiRef.current;
      if (!api) return;
      api.setGridOption("quickFilterText", text);
      dispatch(rSetAgGridState({ key, data: { quickFilter: text } }));
    },
    [dispatch, key]
  );

  /** Get count of rows after current filters */
  const getFilteredRowCount = useCallback(() => {
    const api = apiRef.current;
    if (!api) return 0;
    let count = 0;
    api.forEachNodeAfterFilter(() => count++);
    return count;
  }, []);

  /**
   * Handlers to wire onto the grid for persistence.
   * Tip: For resize, we only persist when the event is "finished".
   */
  const onColumnResized = useCallback(
    (e: ColumnResizedEvent) => {
      if (e.finished) onStateUpdated();
    },
    [onStateUpdated]
  );

  // Bundle of all event handlers you should plug into <AgGridReact/>
  const gridStateHandlers = useMemo(
    () => ({
      onGridReady,
      onSortChanged: onStateUpdated,
      onFilterChanged: onStateUpdated,
      onColumnMoved: onStateUpdated,
      onColumnVisible: onStateUpdated,
      onColumnPinned: onStateUpdated,
      onColumnResized, // special handling
      onColumnRowGroupChanged: onStateUpdated,
      onColumnPivotChanged: onStateUpdated,
      onColumnValueChanged: onStateUpdated,
      onPivotModeChanged: onStateUpdated,
    }),
    [onGridReady, onStateUpdated, onColumnResized]
  );

  return {
    /** Attach these directly to <AgGridReact {...gridStateHandlers} /> */
    gridStateHandlers,
    /** Quick filter helpers */
    setQuickFilterText,
    getFilteredRowCount,
  };
}
